/**
 * Extra Maps JS libraries requested on `<APIProvider>` (`Map.tsx`).
 *
 * Empty as of VERA-260: this used to request `"visualization"`, solely for
 * `google.maps.visualization.HeatmapLayer` — which Google REMOVED in Maps JS v3.65
 * (the symbol stayed on the namespace, so it threw only when constructed; see
 * `Map.tsx`'s heat-mode effect). Heat mode now renders via deck.gl's `HeatmapLayer`
 * through `GoogleMapsOverlay`, neither of which is a Maps JS "library" — they are
 * ordinary npm packages, lazy-imported only when heat mode is actually used (see the
 * same effect). Nothing else in this component requests a Maps JS library, so this is
 * empty rather than removed: a future need (e.g. `"places"`) has one obvious place to
 * add it, and every consumer already gets it for free the same way `visualization` did.
 */
export const MAP_EXTRA_LIBRARIES: string[] = [];

export const MAP_STYLES = {
  light: [
    {
      elementType: "geometry",
      stylers: [{ color: "#edf0ef" }],
    },
    {
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#707070" }],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#f5f6f5" }],
    },
    {
      featureType: "administrative",
      elementType: "geometry.stroke",
      stylers: [{ color: "#d9dcda" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#e9ece9" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#d8e2d8" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#f8f8f8" }],
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [{ color: "#f2f3f2" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#e0e2e1" }],
    },
    {
      featureType: "road.local",
      elementType: "geometry",
      stylers: [{ color: "#fafafa" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#c5cfdd" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9ba2a9" }],
    },
  ] as google.maps.MapTypeStyle[],

  // Neutral near-black dark theme (no blue tint). Land is a warm-neutral #17181a;
  // water is a near-black #0e0f11 so oceans don't read as blue-grey in the world view.
  dark: [
    {
      elementType: "geometry",
      stylers: [{ color: "#17181a" }],
    },
    {
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#bdbdbd" }],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#141414" }],
    },
    {
      featureType: "administrative",
      elementType: "geometry.stroke",
      stylers: [{ color: "#2b2b2b" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#212123" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#1c2620" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#282828" }],
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [{ color: "#2f2f2f" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#343434" }],
    },
    {
      featureType: "road.local",
      elementType: "geometry",
      stylers: [{ color: "#222222" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#0e0f11" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6a6f75" }],
    },
  ] as google.maps.MapTypeStyle[],

  "vitamin-c": [
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#004358" }],
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#fd7400" }],
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }, { lightness: -20 }],
    },
    {
      featureType: "road.local",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }, { lightness: -17 }],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#ffffff" }, { visibility: "on" }, { weight: 0.9 }],
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ visibility: "on" }, { color: "#ffffff" }],
    },
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "simplified" }],
    },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    {
      featureType: "transit",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }, { lightness: -10 }],
    },
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: [{ color: "#1f8a70" }, { weight: 0.7 }],
    },
  ] as google.maps.MapTypeStyle[],
};

export const DEFAULT_MAP_CONFIG = {
  defaultZoom: 4,
  height: "600px",
  mapStyle: "light" as const,
  enableClustering: true,
  showLoadingState: true,
  showErrorState: true,
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "greedy" as const,
  emptyMessage: "No locations available yet",
  errorMessage: "Google Maps API key not configured",
};

/**
 * deck.gl `HeatmapLayer` tuning, `renderMode="heat"` (`Map.tsx`).
 *
 * `HEATMAP_RADIUS` -> `radiusPixels`, deck.gl's screen-space aggregation radius
 * (default 30). `HEATMAP_OPACITY` -> the layer's base `opacity`. Carried over as the
 * starting point from the previous Google `HeatmapLayer`'s tuned `radius`/`opacity`
 * (that layer's `radius` was Google's own default of 10 scaled up, because scan data is
 * comparatively sparse per pixel next to the point-cloud examples Google's demos tune
 * against — at the default radius a company's whole spread read as disconnected dots
 * rather than a continuous surface). deck.gl's radius is not guaranteed to read
 * identically at the same pixel value — the two renderers compute density differently
 * (screen-space Gaussian kernel here vs. Google's dissipating-radius model) — so this is
 * a starting point for a real visual pass against production data, not a proven match.
 * Named here rather than left implicit so both numbers are one place to retune together.
 */
export const HEATMAP_RADIUS = 24;
export const HEATMAP_OPACITY = 0.7;

/**
 * `debounceTimeout` — how long a viewport change waits before the density surface is
 * re-aggregated. **deck.gl's default is 500ms and that is what made the heat map "take a
 * while to reload when zooming."**
 *
 * The mechanism, from the layer's own source (`_debouncedUpdateWeightmap`): a viewport
 * change does NOT recompute the weight texture. It clears `isWeightMapDirty` and schedules
 * the real recompute `debounceTimeout` later — so for that whole window the GPU keeps
 * drawing the PREVIOUS aggregation, stretched to the new zoom, and then snaps. The delay
 * is not the aggregation being slow; it is the layer deliberately waiting.
 *
 * That default is tuned for the datasets deck.gl demos against — hundreds of thousands to
 * millions of points, where re-aggregating mid-gesture genuinely stutters. A company's
 * scan set is three orders of magnitude smaller (thousands), and aggregation is a single
 * GPU pass over the points into one texture, so paying it several times during a zoom
 * costs nothing measurable and buys back the stall.
 *
 * Not zero: at 0 every intermediate frame of a continuous pinch/scroll triggers its own
 * pass, which is real work for no benefit since none of those frames is where the user is
 * headed. ~100ms is below the threshold where a redraw reads as a separate event, while
 * still collapsing a fast gesture into a handful of passes rather than one per frame.
 *
 * ⚠️ Revisit this alongside `weightsTextureSize` (left at deck.gl's 2048 default) if the
 * point count ever grows by an order of magnitude — that is the knob that trades spatial
 * precision for aggregation cost, and it is the correct one to reach for before putting
 * the debounce back up.
 */
export const HEATMAP_DEBOUNCE_MS = 100;

/**
 * The density ramp, low -> high, as deck.gl `colorRange` RGBA stops.
 *
 * Brand purple (`palette.brand` 950 -> 300), replacing deck.gl's default yellow-to-red.
 * Two reasons, and the second is the one that actually matters:
 *
 * 1. It is the product's colour. A dashboard whose every other accent is brand purple
 *    should not have one card rendering in an unrelated palette.
 *
 * 2. ⚠️ RED IS ALREADY TAKEN ON THIS MAP, and it means something incompatible. In pins
 *    mode red is the INVALID/TAMPERED scan. A ramp whose hot end is red therefore paints
 *    "this is where the most scanning happens" in the exact colour that elsewhere means
 *    "this is where something is wrong" — so a brand's busiest, healthiest city renders
 *    as its most alarming one. The two readings are not merely different, they are
 *    opposite, and nothing on screen tells you which one you are looking at.
 *
 * Ascending in LIGHTNESS, not just saturation, because the basemap is dark: on a dark
 * surface the eye reads brightness as magnitude, so the hot core has to be the lightest
 * stop rather than the most-saturated one. (A ramp that gets darker as it intensifies
 * would fight the background and vanish into it at the top end.)
 *
 * Single-hue on purpose. Sequential single-hue ramps are the correct encoding for a
 * magnitude variable; multi-hue "rainbow"/jet ramps introduce boundaries the data does
 * not have, because hue is read as categorical. That single-hue is also the accessible
 * choice here is a bonus rather than the reason — it stays legible under every common
 * colour-vision deficiency, since it varies in lightness rather than in the red/green
 * axis that ~8% of men cannot separate.
 *
 * The lowest stop is deliberately semi-transparent: deck.gl's `threshold` culls the
 * faintest cells outright, but the first surviving band still paints, and at full alpha
 * a sparse spread lays a flat purple wash over the whole basemap that reads as coverage
 * rather than as low density.
 */
export const HEATMAP_COLOR_RANGE: [number, number, number, number][] = [
  [52, 25, 103, 90], //  brand.950, softened — barely-there
  [93, 45, 170, 150], // brand.800
  [111, 54, 203, 200], // brand.700
  [127, 72, 223, 230], // brand.600 — the brand's own stop
  [143, 104, 234, 245], // brand.500
  [199, 187, 247, 255], // brand.300 — the hot core
];

/**
 * Size premium a coincident-point stack (`Map.tsx`) and a cluster (below) both apply —
 * the extra canvas the "+N" pill needs, which is why the dot itself is drawn undersized
 * by this same factor (`dotRadiusForPill`). One constant, exported, so a stack and a
 * cluster cannot independently drift to different premiums and end up different sizes
 * for the same count.
 *
 * ⚠️ RAISED 1.6 -> 2.2 (VERA-260, "the cluster numbers are too small to read"), and the
 * reason is worth stating because the obvious lever is the wrong one.
 *
 * The count's ON-SCREEN size is `(fontSize / 44) * dotSizeForZoom(zoom) * premium`, so
 * both the font size and this premium multiply it — but they are NOT interchangeable.
 * The DOT is immune to the premium by construction (`dotRadiusForPill` divides by the
 * same factor, so the premium cancels and the dot always lands at lone-pin size), which
 * means raising the premium buys VIEWBOX HEADROOM for the pill without touching the dot.
 * Raising only the font size does not: a legible count needs a pill roughly 2.7x its
 * font size wide, so at the old 1.6 the font size required (~15 units) would have made a
 * three-digit pill ~40 of the 44 available units, and a four-digit one impossible.
 *
 * 2.2 also lands the geometry on a convenient identity: at world zoom `dotSizeForZoom`
 * returns its 20 floor, so `round(20 * 2.2)` = 44 and one viewBox unit is exactly one
 * CSS pixel. Every number in `dotPillSvg` below can therefore be read as its literal
 * world-zoom pixel size.
 *
 * The cost is transparent canvas: the marker's raster box grows from 32px to 44px at
 * world zoom while the visible content does not, so the dot (drawn at cy=26 in a box
 * anchored at its centre) sits 4.0px below the true coordinate rather than 2.9px. Left
 * alone deliberately — re-anchoring on the dot would fix it, but the anchor is shared
 * with the single-pin marker whose SVG has different geometry, and 1.1px is well below
 * the threshold at which anything on this map is read positionally.
 */
export const STACK_SIZE_PREMIUM = 2.2;

/**
 * Shared "status dot + count pill" geometry — the visual language a coincident-point
 * STACK (`Map.tsx`'s `createStackDotSvg`) and a clustered group of nearby pins (below)
 * both render aggregation as. One function so the two cannot drift apart later
 * (working-conventions §36 — the second hand-roll is the trigger to extract).
 *
 * Always a 44x44 viewBox — the icon is rasterised at exactly that box and anything
 * outside it is CLIPPED, which is why the dot radius is passed in already undersized
 * (see `dotRadiusForPill`) and the pill's right edge stops at x=42.
 *
 * ## Type size (VERA-260): 10 -> 12 units, which is 7.3px -> 12.0px on screen
 *
 * The old 10 was never 10px: at world zoom the marker rasterised into a 32px box, so the
 * 44-unit viewBox scaled by 0.727 and the count landed at **7.3 CSS px** — below the
 * ~10-13px Google's own basemap place labels use, so the number the map exists to
 * communicate was set smaller than the road names it sits on top of. With the premium
 * now at 2.2 the box is 44px at world zoom (1 unit = 1px), so 12 units is **12.0px** —
 * at or just above the basemap's own label size, which is the correct relationship: this
 * is foreground data, the labels are chrome.
 *
 * ⚠️ There IS an upper bound and it is the pill's WIDTH, not the font's height. A pill
 * must be ~2.7x its font size wide to hold three characters ("+47"), so the on-screen
 * pill dimensions are fixed the moment the target font size is — you cannot buy a bigger
 * number without a proportionally bigger pill, whichever knob you turn. At 12 units the
 * three-digit pill is 32 of 44 units, which leaves the dot fully clear (pill bottom
 * y=18, dot top y=22.8) and keeps two adjacent clusters from colliding: the clusterer's
 * default 60px grid separates them by more than the ~32px a pill spans. Going much past
 * 12 breaks the four-digit case ("+1204" already needs 38 of the 44 units).
 *
 * ## Theming: `isDark` inverts the pill, and the polarity is NOT `mapStyle !== 'light'`
 *
 * A white pill is legible on ANY basemap; a zinc pill is legible only on a dark one. So
 * the fail-safe direction is to require a POSITIVE dark signal — the caller passes
 * `isDark` only for `mapStyle === 'dark'`, leaving `vitamin-c` (saturated teal/green)
 * and `custom` (unknowable here) on the white pill. This is deliberately the opposite
 * grouping to the `mapStyle === "light" ? … : …` chrome code in `Map.tsx`, which is
 * choosing a panel background where "not light" is a safe default; on the map tiles
 * themselves it is not.
 *
 * Dark pair is zinc-800 `#27272A` on zinc-900-ish tiles, not zinc-900 — `#18181B` IS the
 * dark basemap's own background (see `Map.tsx`), so a zinc-900 pill would dissolve into
 * it. Text `#FAFAFA`, the same token this map already uses for dark-mode text, giving a
 * contrast ratio of **14.3:1** (pure white would be 14.9:1 — the difference is not what
 * carries this, the fill's darkness is).
 */
export const dotPillSvg = (
  count: number,
  color: string,
  dotRadius: number,
  isDark = false,
): string => {
  const label = `+${count}`;
  // Grow the pill with the digit count so "+120" isn't cramped against the rounded ends.
  // Widths track the 12-unit font: ~7.2 units per glyph plus ~5 units of padding a side.
  const pillW = label.length <= 2 ? 26 : label.length === 3 ? 32 : label.length === 4 ? 38 : 43;
  const pillX = 42 - pillW;
  const pillFill = isDark ? "#27272A" : "#FFFFFF";
  // Zinc-600, NOT zinc-700. The dark pill's own fill sits at only 1.19:1 against the
  // dark basemap's base geometry (`#17181A`), so the fill alone does not draw the chip's
  // edge — the border has to. Zinc-700 `#3F3F46` measured 1.43:1 against the pill and
  // 1.70:1 against the basemap, i.e. nearly nothing in either direction, which left the
  // digits looking like they floated rather than sat on a chip. Zinc-600 gives 1.93:1 /
  // 2.30:1: enough to delineate, still well short of reading as a bright ring that would
  // compete with the count. (The light side's `#D9D9D9`-on-white is only 1.32:1, and
  // deliberately so — a light chip is separated by its FILL, which is why it needs less
  // from its border. The two are not meant to match numerically.)
  const pillStroke = isDark ? "#52525B" : "#D9D9D9";
  const pillText = isDark ? "#FAFAFA" : "#18181B";
  return `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <defs>
        <filter id="dot-pill-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>
      <circle cx="22" cy="26" r="${dotRadius + 2}" fill="${color}" opacity="0.55" filter="url(#dot-pill-glow)" />
      <circle cx="22" cy="26" r="${dotRadius}" fill="${color}" />
      <rect x="${pillX}" y="1" width="${pillW}" height="17" rx="8.5" fill="${pillFill}" stroke="${pillStroke}" stroke-width="1" />
      <text x="${pillX + pillW / 2}" y="9.9" text-anchor="middle" dominant-baseline="central" font-family="Inter, Arial, sans-serif" font-size="12" font-weight="600" fill="${pillText}">${label}</text>
    </svg>`;
};

/**
 * The dot's radius, undersized by `sizePremium` so that AFTER the marker's own
 * `scaledSize` applies that SAME premium, the dot lands at exactly the same ON-SCREEN
 * size as a lone pin — never bigger, which is what made the old flat disc misrepresent
 * one spot as more important than its neighbours. `7` is the lone-pin dot radius in the
 * same 44-unit space (`Map.tsx`'s `createCustomMarkerSvg`).
 */
export const dotRadiusForPill = (sizePremium: number): number => 7 / sizePremium;

/**
 * Cluster marker — several nearby (not coincident) pins collapsed at this zoom.
 *
 * ⚠️ This used to be a flat WHITE disc with a dark count, ALWAYS — deliberately never
 * tinted by its most-alerting member, on the reasoning that a solid coloured disc was
 * the single loudest mark on the map and overclaimed "there is a problem HERE" for what
 * a cluster actually means ("one of the N things under this circle is a problem"). That
 * traded away exactly the signal this map exists to surface: a cluster hiding a
 * TAMPERED or INVALID scan read identical to a cluster of entirely clean ones, and
 * stayed that way until the user zoomed in far enough to break it apart.
 *
 * Reversed 2026-08-30 (VERA-260, "the pin aggregators on map look really bad"): render a
 * cluster with the SAME visual language a coincident-point STACK already uses
 * (`dotPillSvg` above) — a normal status-coloured dot plus a small "+N" pill — rather
 * than inventing a second aggregation language. The dot takes the colour of the
 * cluster's most-alerting member (`accentColor`, already computed from each marker's
 * `clusterPriority`/`clusterColor` in `Map.tsx` and passed here — no second severity
 * path); a cluster of entirely clean scans naturally renders in the calm default colour,
 * the same as any other clean pin.
 *
 * Sizing (never larger than a lone pin) is the CALLER's job, not this function's: the
 * caller applies the same `STACK_SIZE_PREMIUM` premium to the marker's `scaledSize` a
 * stack already does, reading the CURRENT zoom fresh at render time (see `Map.tsx`'s
 * `MarkerClusterer` renderer) rather than a size this module has no way to know.
 */
export const createClusterSvg = (
  count: number,
  accentColor?: string,
  isDark = false,
): string => {
  // "#7F48DF" mirrors `DEFAULT_PIN_COLOR` in `Map.tsx` — duplicated as a literal rather
  // than imported to avoid a circular import (`Map.tsx` imports FROM this file).
  // `MapDrawLayer.tsx`'s own `color` default carries the same literal for the same
  // reason. `#8F68EA` is the dark-mode lift `getScanStatus` applies to the same hue, so
  // an UNACCENTED cluster (every member clean, no colour passed) does not stay at the
  // light-mode purple on a dark basemap while its accented neighbours move.
  const fill = accentColor || (isDark ? "#8F68EA" : "#7F48DF");
  return dotPillSvg(count, fill, dotRadiusForPill(STACK_SIZE_PREMIUM), isDark);
};
